// src/components/Toolbar.js

import React, { useReducer, useContext, useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar as MuiToolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  Box,
  IconButton,
  Badge,
} from "@mui/material";
import { ExpandMore, AccountCircle, Notifications as NotificationsIcon } from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import UserContext from "../context/UserContext";
import { useRefresh } from "../context/RefreshContext";
import { RoleMapping } from "../constants/roles";
import { mainMenuItems, adminMenuItems, mainAddMenuItems, adminAddMenuItems } from "../constants/menuItems";
import { menuLabelMap } from "../constants/menuLabelMap";
import logo from "../images/logo.png";
import SearchIcon from "@mui/icons-material/Search";
import SearchDrawer from "./SearchDrawer";
import AddModal from "./AddModal";
import api from "../services/api";

const initialState = {
  anchorElLeft: null,
  anchorElCenter: null,
  anchorElRight: null,
};

function reducer(state, action) {
  switch (action.type) {
    case "OPEN_MENU":
      return { ...state, [action.menu]: action.anchorEl };
    case "CLOSE_MENU":
      return { ...state, [action.menu]: null };
    default:
      return state;
  }
}

function Toolbar() {
  const { user, loading } = useContext(UserContext);
  const location = useLocation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const userRoleName = user ? RoleMapping[user.role] : null;
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const pathSegments = location.pathname.split("/");
  const customerId = pathSegments[1] === "customers" ? Number(pathSegments[2]) : null;
  const petId = pathSegments[1] === "pets" ? Number(pathSegments[2]) : null;
  const [preselectedCustomer, setPreselectedCustomer] = useState(null);
  const [preselectedPet, setPreselectedPet] = useState(null);
  const { refreshCounter } = useRefresh();
  const [notificationCount, setNotificationCount] = useState();
  const addButtonRef = useRef(null);

  useEffect(() => {
    if (!isAddModalOpen && addButtonRef.current) {
      setTimeout(() => {
        addButtonRef.current.blur();
      }, 100);
    }
  }, [isAddModalOpen]);

  useEffect(() => {
    const eventSource = new EventSource("/api/notifications/new-submissions");
  
    eventSource.onmessage = (event) => {
      try {
        const parsedData = JSON.parse(event.data);
        //console.log("SSE event received:", parsedData);
        setNotificationCount(parsedData.count);
      } catch (err) {
        console.error("Error parsing SSE event data:", err);
      }
    };
  
    eventSource.onerror = (error) => {
      console.error("EventSource encountered an error:", error);
    };
  
    return () => {
      eventSource.close();
    };
  }, []);

  const handleMenuOpen = (menu) => (event) => {
    dispatch({ type: "OPEN_MENU", menu, anchorEl: event.currentTarget });
  };

  const handleMenuClose = (menu) => () => {
    dispatch({ type: "CLOSE_MENU", menu });
  };

  const handleSearch = (category, searchTerm) => {
    console.log("Searching in:", category, "for:", searchTerm);

    setIsSearchOpen(false);

    navigate(`/search?category=${category}&query=${encodeURIComponent(searchTerm)}`);
  };

  useEffect(() => {
    if (customerId) {

      const fetchCustomer = async () => {
        try {
          const token = localStorage.getItem("token");
          const response = await api.get(`/customers/${customerId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setPreselectedCustomer(response.data);

          if (response.data.pets.length === 1) {
            setPreselectedPet(response.data.pets[0]);
          } else {
            setPreselectedPet(null);
          }

        } catch (error) {
          console.error("Error fetching customer:", error);
        }
      };
      fetchCustomer();
    } else {
      setPreselectedCustomer(null);
    }

    if (petId) {

      const fetchPet = async () => {
        try {
          const token = localStorage.getItem("token");
          const response = await api.get(`/pets/${petId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setPreselectedPet(response.data);
          setPreselectedCustomer(response.data.owner); // Also set the pet's owner as preselectedCustomer
        } catch (error) {
          console.error("Error fetching pet:", error);
        }
      };
      fetchPet();
    } else {
      setPreselectedPet(null);
    }
  }, [customerId, petId, refreshCounter]);

  // Determine the current base path (e.g., '/customers', '/pets')
  const basePath = `/${pathSegments[1]}`;
  const currentLabel = menuLabelMap[basePath] || "Customers"; // Default to 'Customers' if path not found

  // Combine main menu items with admin menu items if user is admin
  const combinedMenuItems = [...mainMenuItems];
  if (userRoleName === "admin") {
    combinedMenuItems.push(...adminMenuItems);
  }

  // Combine main "Add" menu items with admin ones if user is admin
  const combinedAddMenuItems = [...mainAddMenuItems];
  if (userRoleName === "admin") {
    combinedAddMenuItems.push(...adminAddMenuItems);
  }

  // Handler for clicking the notification bell (optional)
  const handleNotifications = () => {
    // For example, navigate to a notifications page or open a drawer.
    navigate("/notifications");
  }

  return (
    <AppBar position="fixed">
      <Box sx={{ height: "42px" }} />
      <MuiToolbar variant="dense" sx={{ px: 2 }}>
        {/* Left Side */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* Logo */}
          <Typography
            variant="h6"
            component={Link}
            to="/customers"
            sx={{
              color: "inherit",
              textDecoration: "none",
              mr: 2
            }}
          >
            <img
              src={logo}
              alt="Your Logo"
              style={{ height: 30, display: "block" }}
            />
          </Typography>
          {/* Left Dropdown Menu */}
          <Button
            color="inherit"
            onClick={handleMenuOpen("anchorElLeft")}
            endIcon={<ExpandMore />}
          >
            {currentLabel}
          </Button>
          <Menu
            anchorEl={state.anchorElLeft}
            open={Boolean(state.anchorElLeft)}
            onClose={handleMenuClose("anchorElLeft")}
          >
            {combinedMenuItems.map((item) => (
              <MenuItem
                key={item.path}
                component={Link}
                to={item.path}
                onClick={handleMenuClose("anchorElLeft")}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Center */}
        <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center", lm: 3 }}>
          <Button
            ref={addButtonRef}
            color="inherit"
            onClick={handleMenuOpen("anchorElCenter")}
            endIcon={<ExpandMore />}
          >
            Add
          </Button>
          <Menu
            anchorEl={state.anchorElCenter}
            open={Boolean(state.anchorElCenter)}
            onClose={handleMenuClose("anchorElCenter")}
          >
            {combinedAddMenuItems.map((item) => (
              <MenuItem
                key={item.path}
                onClick={() => {
                  setSelectedCategory(item.label);
                  setIsAddModalOpen(true);
                  handleMenuClose("anchorElCenter")();
                }}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Right Side */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* Bell Icon with Badge next to Search */}
          <IconButton color="inherit" onClick={handleNotifications} sx={{ mr: 4 }}>
            <Badge
              badgeContent={notificationCount}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.7rem',
                  minWidth: 10,
                  height: 16,
                  top: -10,
                  right: 2,
                  transform: 'scale(0.9) translate(50%, 50%)',
                },
              }}
            >
              <NotificationsIcon sx={{ fontSize: '24px' }} />
            </Badge>
          </IconButton>
          {/* Search Field (Placeholder) */}
          <Button
            color="inherit"
            startIcon={<SearchIcon />}
            onClick={() => setIsSearchOpen(true)}
            sx={{ mr: 6 }}
          >
            Search
          </Button>
          {/* User Dropdown Menu */}
          <Button
            color="inherit"
            onClick={handleMenuOpen("anchorElRight")}
            endIcon={<ExpandMore />}
            startIcon={<AccountCircle />}
          >
            {loading ? "Loading..." : user?.name || "Guest"}
          </Button>
          <Menu
            anchorEl={state.anchorElRight}
            open={Boolean(state.anchorElRight)}
            onClose={handleMenuClose("anchorElRight")}
          >
            <MenuItem
              component={Link}
              to="/edit-profile"
              onClick={handleMenuClose("anchorElRight")}
            >
              Edit Profile
            </MenuItem>
            <MenuItem component={Link} to="/logout" onClick={handleMenuClose("anchorElRight")}>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </MuiToolbar>
      {/* Search Drawer */}
      {isSearchOpen && (
        <SearchDrawer
          open={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          onSearch={handleSearch}
          userRoleName={userRoleName}
        />
      )}
      {isAddModalOpen && (
        <AddModal
          open={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          initialCategory={selectedCategory}
          preselectedCustomer={preselectedCustomer}
          preselectedPet={preselectedPet}
        />
      )}
    </AppBar>

  );
}

export default Toolbar;